import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Button,
  MenuItem,
  Chip,
  Stack,
  Divider,
  ListItemIcon,
  ListItemText,
  alpha
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Store as StoreIcon,
  People as PeopleIcon,
  Category as CategoryIcon,
  Inventory as InventoryIcon,
  PointOfSale as PointOfSaleIcon,
  SupervisorAccount as SuperAdminIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  Logout as LogoutIcon,
  KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);

  // Check if current path matches
  const isActivePath = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  // Only show main navigation if user has completed password change and accepted terms
  const canAccessMainNav = !user?.isFirstLogin && user?.termsAccepted !== false;

  const pages = canAccessMainNav ? (
    user?.isSuperAdmin ? [
      { title: 'Super Admin', path: '/super-admin', icon: <SuperAdminIcon /> }
    ] : [
      { title: 'Dashboard', path: '/app/dashboard', icon: <DashboardIcon /> },
      ...(user?.role === 'admin' ? [
        { title: 'Branches', path: '/app/branches', icon: <StoreIcon /> },
        { title: 'Users', path: '/app/users', icon: <PeopleIcon /> },
        { title: 'Products', path: '/app/products', icon: <CategoryIcon /> }
      ] : []),
      ...(['admin', 'cashier', 'branch_admin'].includes(user?.role) ? [
        { title: 'Sales', path: '/app/sales', icon: <PointOfSaleIcon /> },
      ] : []),
      ...(user?.role === 'admin' ? [
        { title: 'Inventory', path: '/app/inventory', icon: <InventoryIcon /> }
      ] : [])
    ]
  ) : [];

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Filter pages based on user role
  const filteredPages = pages.filter(page => 
    !page.roles || page.roles.includes(user?.role)
  );

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
      elevation={0}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ minHeight: { xs: 56, sm: 64, md: 70 }, px: { xs: 1, sm: 2 } }}>
          {/* Logo and Brand */}
          <Box
            component={Link}
            to="/app/dashboard"
            sx={{
              display: 'flex',
              alignItems: 'center',
              mr: { xs: 1, sm: 2, md: 4 },
              textDecoration: 'none',
              '&:hover': {
                opacity: 0.85,
              }
            }}
          >
            {/* Logo Mark */}
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2.5,
                background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1.5,
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
              }}
            >
              <PointOfSaleIcon sx={{ color: 'white', fontSize: 22 }} />
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Typography
                variant="h6"
                noWrap
                sx={{
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  lineHeight: 1.1,
                }}
              >
                Flex PoS
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.65rem',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                }}
              >
                Point of Sale
              </Typography>
            </Box>
          </Box>

          {/* Mobile Menu */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, flexGrow: 1 }}>
            <IconButton
              size="large"
              aria-label="menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              sx={{
                color: 'text.primary',
                minWidth: 44,
                minHeight: 44,
                '&:hover': {
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                }
              }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{ display: { xs: 'block', md: 'none' } }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: { xs: 240, sm: 220 },
                  maxWidth: '90vw',
                }
              }}
            >
              {filteredPages.map((page) => (
                <MenuItem
                  key={page.title}
                  onClick={handleCloseNavMenu}
                  component={Link}
                  to={page.path}
                  selected={isActivePath(page.path)}
                  sx={{
                    borderRadius: 2,
                    mx: 1,
                    my: 0.5,
                    py: 1.5,
                    minHeight: 48,
                  }}
                >
                  <ListItemIcon sx={{ color: isActivePath(page.path) ? 'primary.main' : 'text.secondary' }}>
                    {page.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={page.title}
                    primaryTypographyProps={{
                      fontWeight: isActivePath(page.path) ? 600 : 500,
                      color: isActivePath(page.path) ? 'primary.main' : 'text.primary',
                    }}
                  />
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 0.5 }}>
            {filteredPages.map((page) => {
              const isActive = isActivePath(page.path);
              return (
                <Button
                  key={page.title}
                  component={Link}
                  to={page.path}
                  onClick={handleCloseNavMenu}
                  startIcon={page.icon}
                  sx={{
                    color: isActive ? 'primary.main' : 'text.secondary',
                    fontWeight: isActive ? 600 : 500,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    position: 'relative',
                    backgroundColor: isActive ? (theme) => alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                      color: 'primary.main',
                    },
                    '& .MuiButton-startIcon': {
                      color: isActive ? 'primary.main' : 'text.secondary',
                    },
                    '&:hover .MuiButton-startIcon': {
                      color: 'primary.main',
                    },
                  }}
                >
                  {page.title}
                </Button>
              );
            })}
          </Box>

          {/* User Section */}
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ flexShrink: 0 }}
          >
            {/* User Info & Avatar Button */}
            <Box
              onClick={handleOpenUserMenu}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.5, sm: 1.5 },
                cursor: 'pointer',
                borderRadius: 3,
                py: 0.75,
                px: { xs: 0.75, sm: 1.5 },
                minHeight: 44,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
                },
              }}
            >
              <Avatar
                sx={{
                  bgcolor: 'primary.main',
                  width: 38,
                  height: 38,
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  boxShadow: '0 2px 8px rgba(99, 102, 241, 0.25)',
                }}
              >
                {user?.firstName?.[0]?.toUpperCase() || 'U'}
              </Avatar>
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'text.primary',
                    fontWeight: 600,
                    lineHeight: 1.2,
                  }}
                >
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Chip
                  label={user?.isSuperAdmin ? 'Super Admin' : user?.role?.replace('_', ' ')}
                  size="small"
                  sx={{
                    height: '20px',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    backgroundColor: user?.isSuperAdmin
                      ? (theme) => alpha(theme.palette.error.main, 0.1)
                      : user?.role === 'admin'
                        ? (theme) => alpha(theme.palette.primary.main, 0.1)
                        : 'grey.100',
                    color: user?.isSuperAdmin
                      ? 'error.main'
                      : user?.role === 'admin'
                        ? 'primary.main'
                        : 'text.secondary',
                    border: 'none',
                    '& .MuiChip-label': {
                      px: 1,
                    }
                  }}
                />
              </Box>
              <ArrowDownIcon
                sx={{
                  color: 'text.secondary',
                  fontSize: 18,
                  display: { xs: 'none', md: 'block' }
                }}
              />
            </Box>

            {/* User Menu */}
            <Menu
              sx={{ mt: '8px' }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
              PaperProps={{
                sx: {
                  minWidth: 220,
                  p: 1,
                }
              }}
            >
              {/* User Info Header in Menu */}
              <Box sx={{ px: 2, py: 1.5, display: { xs: 'block', md: 'none' } }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>
              <Divider sx={{ my: 1, display: { xs: 'block', md: 'none' } }} />

              <MenuItem
                onClick={handleCloseUserMenu}
                component={Link}
                to="/app/profile/settings"
                sx={{ borderRadius: 2 }}
              >
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Profile Settings" />
              </MenuItem>
              <MenuItem
                onClick={handleCloseUserMenu}
                component={Link}
                to="/change-password"
                sx={{ borderRadius: 2 }}
              >
                <ListItemIcon>
                  <LockIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Change Password" />
              </MenuItem>

              <Divider sx={{ my: 1 }} />

              <MenuItem
                onClick={handleLogout}
                sx={{
                  borderRadius: 2,
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
                  }
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </MenuItem>
            </Menu>
          </Stack>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;